import { Card } from '@mdvip/shared/components/ui/card';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { ProfileAvatar } from '@/components/profile/ProfileAvatar';
import type { MemberProfile } from '@/hooks/profile/profileApi';

interface ProfileOverviewCardProps {
  member: MemberProfile;
  onEditPhoto: () => void;
}

function fullName(firstName: string | null, lastName: string | null): string {
  return [firstName, lastName].filter(Boolean).join(' ');
}

/**
 * The member's identity card in the Profile rail — photo (via `ProfileAvatar`) beside the name and
 * Member ID. Matches the design's `CardProfileOverview`, the card `ProfileApp direction="D"` (the
 * screen this app actually mounts) puts the photo in — not `ProfileDetailsCard`'s own `showIdentity`
 * mode, which no real screen direction turns on.
 */
export function ProfileOverviewCard({ member, onEditPhoto }: ProfileOverviewCardProps) {
  const { t } = useTranslation('members');
  const name = fullName(member.firstName, member.lastName) || t('profile.notOnFile');

  return (
    <Card className="flex flex-col gap-4 p-6" data-slot="card-profile-overview">
      <div className="flex items-center gap-4">
        <ProfileAvatar
          firstName={member.firstName}
          lastName={member.lastName}
          photoUrl={member.photoUrl}
          onEditPhoto={onEditPhoto}
        />
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-lg font-semibold text-foreground">{name}</h2>
          {member.mdvipId && (
            <div className="mt-1 text-xs text-muted-foreground">
              {t('profile.overview.memberId', { id: member.mdvipId })}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

/** Loading placeholder — matches the design's own inline skeleton for this card. */
export function ProfileOverviewCardSkeleton() {
  return (
    <Card className="flex flex-col gap-4 p-6" data-slot="card-profile-overview-skeleton" aria-busy="true">
      <div className="flex items-center gap-4">
        <Skeleton className="size-14 shrink-0 rounded-full" />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
    </Card>
  );
}
